import { Box, Input, Link, Stack, Text } from '@chakra-ui/react';
import React from 'react';
import { PillButton, ResendControl, StatusPanel, SuccessPanel } from './primitives';

export const ExpiredLinkPanel = ({
  knownEmail,
  onSendNew,
  onSignInAnotherWay,
}: {
  knownEmail?: string | null;
  onSendNew: (identifier: string) => Promise<void> | void;
  onSignInAnotherWay: () => void;
}) => {
  const [identifier, setIdentifier] = React.useState(knownEmail ?? '');
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const send = async () => {
    setError(null);
    if (!identifier.trim()) {
      setError('Enter the email or mobile number you used.');
      return;
    }
    setBusy(true);
    try {
      await onSendNew(identifier.trim());
      setSent(true);
    } catch {
      setError("We couldn't send a new link just now. Please try another way.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <SuccessPanel
        heading="New link sent"
        body={`Check ${identifier}. It'll work for 15 minutes.`}
        actions={<ResendControl onResend={send} />}
      />
    );
  }

  return (
    <StatusPanel
      eyebrow="Link expired"
      heading="That link has already been used or timed out"
      body={
        knownEmail
          ? "Sign-in links last 15 minutes and work once. Nothing's wrong with your account — we'll send a fresh one."
          : 'Sign-in links last 15 minutes and work once. Tell us where to send a new one.'
      }
      detail={
        <Stack spacing={2}>
          <Text fontSize="12px" fontWeight={500} color="gray.700">
            {knownEmail ? 'Send a new link to' : 'Email address'}
          </Text>
          <Input
            h="52px"
            borderRadius="6px"
            borderColor={knownEmail ? 'dmNavy.500' : 'gray.200'}
            _focusVisible={{ borderColor: 'dmNavy.500', boxShadow: 'none' }}
            fontSize="16px"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@example.com"
          />
          {!knownEmail && (
            <Text fontSize="12px" color="dmStorm.500">
              The email address on your account. Signed up with your phone? Use “Sign in another
              way.”
            </Text>
          )}
          {error && (
            <Text fontSize="13px" color="red.600" fontWeight={500}>
              {error}
            </Text>
          )}
        </Stack>
      }
      actions={
        <>
          <PillButton
            onClick={send}
            isLoading={busy}
            loadingText="Sending"
            w={{ base: '100%', md: 'auto' }}
            px={7}
          >
            Send me a new link
          </PillButton>
          <PillButton
            pill="navy"
            onClick={onSignInAnotherWay}
            w={{ base: '100%', md: 'auto' }}
            px={6}
          >
            Sign in another way
          </PillButton>
        </>
      }
      footer={
        <Text fontSize="12px" color="dmStorm.500" textAlign={{ base: 'center', md: 'left' }}>
          Password, a text code, Google, or Facebook.
        </Text>
      }
    />
  );
};

/** The ticket is opaque and redeeming it to identify it would spend it — hence neutral copy. */
export const AlreadySignedInPanel = ({
  firstName,
  reservationNumber,
  onContinue,
  onSignOutAndUse,
}: {
  firstName?: string | null;
  reservationNumber?: string | null;
  onContinue: () => void;
  onSignOutAndUse: () => void;
}) => (
  <StatusPanel
    eyebrow="Already signed in"
    heading="You're already signed in"
    body={
      <>
        This link signs in to a Del Mar account
        {reservationNumber ? ` for reservation ${reservationNumber}` : ''}. You're currently signed
        in{firstName ? ` as ${firstName}` : ''} — carry on, or sign out and use the link instead.
      </>
    }
    actions={
      <>
        <PillButton onClick={onContinue} w={{ base: '100%', md: 'auto' }} px={7}>
          Continue as {firstName || 'you'}
        </PillButton>
        <PillButton pill="navy" onClick={onSignOutAndUse} w={{ base: '100%', md: 'auto' }} px={6}>
          Sign out and use this link
        </PillButton>
      </>
    }
    footer={
      <Text fontSize="12px" color="dmStorm.500">
        Nothing is lost either way — the link keeps working until you use it.
      </Text>
    }
  />
);

/** Verified in another tab of this browser: the session exists here, so adopt it. */
export const VerifiedElsewherePanel = ({
  ctaLabel = 'Continue',
  onContinue,
}: {
  ctaLabel?: string;
  onContinue: () => void;
}) => (
  <SuccessPanel
    heading="You opened the link somewhere else"
    body="It worked — you're signed in. You can carry on right here."
    actions={
      <PillButton w="100%" maxW="360px" onClick={onContinue}>
        {ctaLabel}
      </PillButton>
    }
    footer={
      <Text fontSize="12px" color="dmStorm.500">
        The other tab is finished with — you can close it.
      </Text>
    }
  />
);

/** Verified on a different device: this tab genuinely cannot sign in. */
export const VerifiedOnOtherDevicePanel = ({
  onSendFreshLink,
  onSignInAnotherWay,
}: {
  onSendFreshLink: () => void;
  onSignInAnotherWay: () => void;
}) => (
  <StatusPanel
    eyebrow="Signed in elsewhere"
    eyebrowColor="dmNavy.500"
    heading="You're signed in on the device that opened the link"
    body="That link only signs in the device that opens it. To use this one, we'll send a fresh link here."
    detail={
      <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="6px" p="14px">
        <Text fontSize="13px" lineHeight="18px" fontWeight={500} color="dmNavy.500">
          Already on the right device?
        </Text>
        <Text fontSize="13px" lineHeight="18px" color="gray.600">
          Switch back to that tab or window and carry on there.
        </Text>
      </Box>
    }
    actions={
      <>
        <PillButton onClick={onSendFreshLink} w={{ base: '100%', md: 'auto' }} px={7}>
          Send a link to this device
        </PillButton>
        <PillButton
          pill="navy"
          onClick={onSignInAnotherWay}
          w={{ base: '100%', md: 'auto' }}
          px={6}
        >
          Sign in another way
        </PillButton>
      </>
    }
  />
);

export const LinkSentPanel = ({
  email,
  onResend,
}: {
  email?: string | null;
  onResend: () => void;
}) => (
  <SuccessPanel
    heading="Check your email"
    body={`We sent a sign-in link${email ? ` to ${email}` : ''}. It expires in 15 minutes.`}
    actions={<ResendControl onResend={onResend} />}
    footer={
      <Text fontSize="12px" color="dmStorm.500">
        Didn't arrive? Check spam, or send it again above.
      </Text>
    }
  />
);

export const SignInAnotherWayLink = ({ onClick }: { onClick: () => void }) => (
  <Link fontSize="13px" color="dmNavy.500" textDecoration="underline" onClick={onClick}>
    Sign in another way
  </Link>
);
